import { Link } from "react-router-dom";

/**
 * Public-facing privacy policy at /privacy.
 *
 * Linked from the Chrome Web Store listing for the ReservoirAI extension
 * AND from the website footer. Treat the content here as legally binding
 * once shipped — when the policy changes, bump the "Last updated" date
 * and surface a notice in-app for existing users.
 */
const LAST_UPDATED = "May 20, 2026";
const CONTACT_EMAIL = "support@myreservoir.ai";

function Section({ id, title, children }) {
  return (
    <section id={id} className="mt-10">
      <h2 className="font-display text-[20px] font-extrabold tracking-tight">{title}</h2>
      <div className="mt-3 text-[14px] leading-relaxed text-neutral-800 space-y-3">{children}</div>
    </section>
  );
}

export default function Privacy() {
  return (
    <div className="min-h-screen bg-[#fafaf7]">
      <header className="border-b border-black/5 bg-white">
        <div className="max-w-[840px] mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="font-display font-extrabold tracking-tight text-[18px]">
            Reservoir
          </Link>
          <div className="text-[12px] text-neutral-500">Last updated · {LAST_UPDATED}</div>
        </div>
      </header>

      <main className="max-w-[840px] mx-auto px-6 py-10">
        <h1 className="font-display text-[32px] font-extrabold tracking-tight">Privacy Policy</h1>
        <p className="mt-3 text-[14px] text-neutral-700 leading-relaxed">
          This policy describes how Reservoir ("we", "us") handles your data when you use the
          Reservoir website (<span className="font-mono">myreservoir.ai</span>) and the ReservoirAI
          browser extension. By creating an account or installing the extension, you agree to
          the terms below.
        </p>

        <Section id="what" title="What data we collect">
          <p>To keep your loyalty wallet in sync, we store:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong>Account identity</strong> — your email address and (optionally) display name, used to sign you into Reservoir.</li>
            <li><strong>Loyalty balances + program details</strong> — miles, points, tier status, member ID, certificate inventory, qualifying-flight metrics, lifetime balances. Stored per loyalty program you connect.</li>
            <li><strong>Travel data you opt into</strong> — upcoming trips, recent activity, transactions surfaced by the airline/hotel sites you sign in to.</li>
            <li><strong>Sync metadata</strong> — timestamps of last sync, success/failure state, the program code being synced, the URL the extension is currently observing.</li>
            <li><strong>Conversation history</strong> — questions you ask our AI concierge and the responses, so we can keep context across sessions and improve the product.</li>
          </ul>
          <p>
            We <strong>do not</strong> store the username/password for your loyalty programs. The
            extension reads each program's already-rendered account page or API response in your
            own browser after you sign in manually — your credentials never leave your machine.
          </p>
        </Section>

        <Section id="how" title="How the browser extension uses your data">
          <p>
            The ReservoirAI extension intercepts JSON responses on the loyalty programs you have
            connected (currently United, Delta, Southwest, American). Specifically, after you
            sign in on the program's own website, the extension reads the same account-data API
            response the program's own dashboard reads, extracts your balance and related
            fields, and posts those values to your Reservoir account at <span className="font-mono">myreservoir.ai</span>.
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>The extension only activates on tabs you explicitly armed by clicking "Sync via extension" inside Reservoir.</li>
            <li>The extension only forwards a response if its URL matches a per-program endpoint the Reservoir backend has configured.</li>
            <li>The extension does not read or transmit data from any site outside the loyalty programs listed in its <span className="font-mono">host_permissions</span> manifest entry.</li>
            <li>The extension does not collect browsing history, cookies (except a non-empty check on the Reservoir session cookie), or form input.</li>
            <li>The extension does not include any analytics, advertising, or third-party tracking.</li>
          </ul>
        </Section>

        <Section id="ai" title="AI concierge + third-party processors">
          <p>
            When you ask the Reservoir AI concierge a question, your question (and the relevant
            slice of your wallet — typically balances, tier, upcoming trips) is sent to
            Anthropic's Claude API for processing. Anthropic's own privacy policy applies to that
            transmission; Anthropic does not use API data to train their models per their
            commercial terms.
          </p>
        </Section>

        <Section id="storage" title="Storage, security, retention">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>All data is stored on Reservoir's servers in MongoDB, encrypted at rest by the cloud provider.</li>
            <li>Sensitive fields (when applicable) are encrypted with envelope encryption keyed per user.</li>
            <li>We retain account data while your account is active. You can delete your account from Settings; deletion removes balances and conversation history within 30 days.</li>
            <li>The browser extension stores only ephemeral session data in <span className="font-mono">chrome.storage.session</span>, which is cleared automatically when the browser session ends.</li>
          </ul>
        </Section>

        <Section id="sharing" title="What we do not do with your data">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>We do not sell or rent your data to third parties.</li>
            <li>We do not share your data with advertisers.</li>
            <li>We do not use your data to assess credit, lending, or insurance eligibility.</li>
            <li>We do not use your data for purposes unrelated to operating Reservoir.</li>
          </ul>
        </Section>

        <Section id="rights" title="Your rights">
          <p>You can, at any time:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Export your data from <Link to="/settings" className="underline">Settings</Link>.</li>
            <li>Delete your account from <Link to="/settings" className="underline">Settings</Link>; this removes balances, AI conversation history, and any stored loyalty credentials within 30 days.</li>
            <li>Disconnect or remove the browser extension at any time from <span className="font-mono">chrome://extensions</span>. Removing the extension does not delete data already synced to your Reservoir account; deleting your account does.</li>
          </ul>
        </Section>

        <Section id="contact" title="Contact">
          <p>
            Questions, requests, or concerns about this policy or your data: email{" "}
            <a className="underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We
            respond within 5 business days.
          </p>
        </Section>

        <Section id="changes" title="Changes to this policy">
          <p>
            We will update this page when our practices change. Material changes will be
            announced in-app and via email to active users; the "Last updated" date above always
            reflects the most recent revision.
          </p>
        </Section>

        <div className="mt-12 pt-6 border-t border-black/5 text-[12px] text-neutral-500">
          © {new Date().getFullYear()} Reservoir.&nbsp;
          <Link to="/" className="underline">Back to site</Link>
        </div>
      </main>
    </div>
  );
}
